import { useState } from "react"
import toast from "react-hot-toast"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@aix/ui"

import { trpc } from "../lib/trpc"
import { operationBadgeVariant, runContextText } from "./seoBoardHelpers"

interface SEOOperationsTabProps {
  productKey: string
}

const RUN_FILTERS = ["all", "running", "waiting-input", "waiting-approval", "partial", "failed", "succeeded"] as const
type RunFilter = (typeof RUN_FILTERS)[number]
const RELEASE_CHANNELS = ["staging", "production"] as const
type ReleaseChannel = (typeof RELEASE_CHANNELS)[number]

function displayDate(value: Date | string | null | undefined): string {
  if (!value) return "—"
  return new Date(value).toLocaleString()
}

export default function SEOOperationsTab({ productKey }: SEOOperationsTabProps) {
  const [runFilter, setRunFilter] = useState<RunFilter>("all")
  const [runCursor, setRunCursor] = useState<string | undefined>()
  const [sourceCursor, setSourceCursor] = useState<string | undefined>()
  const [releaseCursor, setReleaseCursor] = useState<string | undefined>()
  const [releaseChannel, setReleaseChannel] = useState<ReleaseChannel>("staging")
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const utils = trpc.useUtils()

  const runs = trpc.seo.runs.list.useQuery({
    productKey,
    limit: 20,
    cursor: runCursor,
    state: runFilter === "all" ? undefined : runFilter,
  })
  const sources = trpc.seo.sources.list.useQuery({ productKey, limit: 20, cursor: sourceCursor })
  const releases = trpc.seo.releases.list.useQuery({
    productKey,
    channel: releaseChannel,
    limit: 20,
    cursor: releaseCursor,
  })
  const selectedRun = runs.data?.rows.find((run) => run.id === selectedRunId)
  const receipt =
    selectedRun?.receiptKey && selectedRun.receiptDigest && selectedRun.receiptByteLength !== null
      ? {
          key: selectedRun.receiptKey,
          digest: selectedRun.receiptDigest,
          byteLength: selectedRun.receiptByteLength,
        }
      : null
  const receiptQuery = trpc.seo.runs.readReceipt.useQuery(
    {
      productKey,
      runId: selectedRunId ?? "",
      receipt: receipt ?? { key: "disabled", digest: "0".repeat(64), byteLength: 0 },
    },
    { enabled: Boolean(selectedRunId && receipt) },
  )
  const refreshReleases = () => {
    void utils.seo.releases.list.invalidate()
  }
  const approveRelease = trpc.seo.releases.approve.useMutation({
    onError: (error) => toast.error(error.message || "Release review failed"),
    onSuccess: (release) => {
      toast.success(`Release ${release.id} is ${release.state}`)
      refreshReleases()
    },
  })
  const activateRelease = trpc.seo.releases.activate.useMutation({
    onError: (error) => toast.error(error.message || "Release activation failed"),
    onSuccess: () => {
      toast.success("Release activated")
      refreshReleases()
    },
  })
  const rollbackRelease = trpc.seo.releases.rollback.useMutation({
    onError: (error) => toast.error(error.message || "Release rollback failed"),
    onSuccess: () => {
      toast.success("Release rollback completed")
      refreshReleases()
    },
  })

  return (
    <div className="h-full overflow-auto p-4">
      <div className="mx-auto max-w-6xl space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Agent runs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Durable checkpoints stay in AIX; detailed findings and repair attempts live in a digest-linked R2 receipt.
            </p>
            <div className="flex flex-wrap gap-2">
              {RUN_FILTERS.map((filter) => (
                <Button
                  key={filter}
                  size="sm"
                  variant={runFilter === filter ? "default" : "outline"}
                  onClick={() => {
                    setRunFilter(filter)
                    setRunCursor(undefined)
                    setSelectedRunId(null)
                  }}
                >
                  {filter}
                </Button>
              ))}
            </div>
            {runs.isLoading ? <p className="text-sm text-muted-foreground">Loading runs…</p> : null}
            {runs.error ? <p className="text-sm text-destructive">{runs.error.message}</p> : null}
            {!runs.isLoading && !runs.data?.rows.length ? (
              <p className="text-sm text-muted-foreground">No runs match this filter.</p>
            ) : null}
            <div className="space-y-2">
              {runs.data?.rows.map((run) => (
                <button
                  type="button"
                  key={run.id}
                  className="w-full rounded border-2 border-border bg-background p-3 text-left hover:bg-secondary"
                  onClick={() => setSelectedRunId(run.id === selectedRunId ? null : run.id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-xs">{run.id}</span>
                    <Badge variant={operationBadgeVariant(run.state)}>{run.state}</Badge>
                  </div>
                  <p className="mt-2 text-sm">{runContextText(run.checkpoint, "reason") ?? run.kind}</p>
                  <p className="text-xs text-muted-foreground">
                    Next: {runContextText(run.checkpoint, "nextAction") ?? "Inspect the run receipt."}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {displayDate(run.startedAt ?? run.createdAt)} · {run.costMicros.toLocaleString()} /{" "}
                    {(run.costBudgetMicros ?? 0).toLocaleString()} µUSD
                  </p>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!runCursor} onClick={() => setRunCursor(undefined)}>
                First page
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!runs.data?.nextCursor}
                onClick={() => setRunCursor(runs.data?.nextCursor)}
              >
                Next page
              </Button>
            </div>
            {selectedRun ? (
              <div className="rounded border-2 border-border bg-secondary p-3">
                <h3 className="font-semibold">Run detail</h3>
                {!receipt ? <p className="text-sm text-muted-foreground">No immutable receipt attached yet.</p> : null}
                {receiptQuery.isLoading ? <p className="text-sm text-muted-foreground">Loading receipt…</p> : null}
                {receiptQuery.error ? <p className="text-sm text-destructive">{receiptQuery.error.message}</p> : null}
                {receiptQuery.data ? (
                  <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap text-xs">
                    {JSON.stringify(receiptQuery.data, null, 2)}
                  </pre>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Evidence sources</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sources.isLoading ? <p className="text-sm text-muted-foreground">Loading sources…</p> : null}
              {sources.error ? <p className="text-sm text-destructive">{sources.error.message}</p> : null}
              {sources.data?.rows.map((source) => (
                <div key={source.id} className="rounded border border-border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span>{source.sourceType}</span>
                    <Badge variant={source.publishable ? "success" : "secondary"}>
                      {source.publishable ? "publishable" : "research only"}
                    </Badge>
                  </div>
                  <p className="mt-1 truncate font-mono text-xs">{source.contentFingerprint}</p>
                  <p className="text-xs text-muted-foreground">
                    Rights: {source.rightsStatus} · captured {displayDate(source.capturedAt)}
                  </p>
                </div>
              ))}
              {!sources.isLoading && !sources.data?.rows.length ? (
                <p className="text-sm text-muted-foreground">No persisted evidence sources.</p>
              ) : null}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={!sourceCursor} onClick={() => setSourceCursor(undefined)}>
                  First page
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!sources.data?.nextCursor}
                  onClick={() => setSourceCursor(sources.data?.nextCursor)}
                >
                  Next page
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                Immutable releases
                <span className="flex gap-2">
                  {RELEASE_CHANNELS.map((channel) => (
                    <Button
                      key={channel}
                      type="button"
                      size="sm"
                      variant={releaseChannel === channel ? "default" : "outline"}
                      onClick={() => {
                        setReleaseChannel(channel)
                        setReleaseCursor(undefined)
                      }}
                    >
                      {channel}
                    </Button>
                  ))}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {releaseChannel === "staging"
                  ? "Staging activates only the dev.vidmax.ai pointer and remains noindex."
                  : "Production activation publishes pages and switches the public vidmax.ai pointer."}
              </p>
              {releases.isLoading ? <p className="text-sm text-muted-foreground">Loading releases…</p> : null}
              {releases.error ? <p className="text-sm text-destructive">{releases.error.message}</p> : null}
              {releases.data?.rows.map((release) => (
                <div key={release.id} className="rounded border border-border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs">{release.id}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant="secondary">{release.channel}</Badge>
                      <Badge variant={operationBadgeVariant(release.state)}>{release.state}</Badge>
                    </span>
                  </div>
                  <p className="mt-1">
                    v{release.version} · {release.pageCount} pages · {release.assetCount} assets
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Approval: {release.approval.status} · probe: {release.probeResult?.passed ? "passed" : "waiting"}
                  </p>
                  <details className="mt-2 rounded bg-muted p-2 text-xs text-muted-foreground">
                    <summary className="cursor-pointer font-medium text-foreground">
                      Release identity and rollback detail
                    </summary>
                    <dl className="mt-2 grid gap-1">
                      <div>
                        <dt className="inline">Manifest digest: </dt>
                        <dd className="inline break-all font-mono">{release.manifestDigest ?? "not compiled"}</dd>
                      </div>
                      <div>
                        <dt className="inline">Previous release: </dt>
                        <dd className="inline font-mono">{release.previousReleaseId ?? "none recorded"}</dd>
                      </div>
                      <div>
                        <dt className="inline">Activated: </dt>
                        <dd className="inline">{displayDate(release.activatedAt)}</dd>
                      </div>
                      <div>
                        <dt className="inline">Probe objects: </dt>
                        <dd className="inline">
                          {release.probeResult
                            ? `${release.probeResult.verifiedObjects}/${release.probeResult.expectedObjects}`
                            : "not probed"}
                        </dd>
                      </div>
                    </dl>
                    {release.probeResult?.issues.length ? (
                      <ul className="mt-2 list-disc pl-5 text-destructive">
                        {release.probeResult.issues.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    ) : null}
                  </details>
                  <ReleaseReadinessChecklist
                    pageCount={release.pageCount}
                    assetCount={release.assetCount}
                    manifestReady={Boolean(release.manifestKey && release.manifestDigest)}
                    probePassed={release.probeResult?.passed === true}
                    approved={release.approval.status === "approved"}
                    active={release.state === "active"}
                  />
                  {release.manifestKey && release.manifestDigest ? (
                    <SEOReleaseArtifactReview productKey={productKey} releaseId={release.id} channel={releaseChannel} />
                  ) : null}
                  {release.state === "staged" ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <ReleaseActionDialog
                        title={`Approve release v${release.version}?`}
                        description="This records your MFA-verified approval. It does not make the release public."
                        actionLabel="Approve release"
                        loading={approveRelease.isLoading}
                        onConfirm={() =>
                          approveRelease.mutate({
                            productKey,
                            channel: releaseChannel,
                            id: release.id,
                            approval: { status: "approved", note: "Reviewed in AIX Command Center" },
                          })
                        }
                      />
                      <ReleaseActionDialog
                        title={`Reject release v${release.version}?`}
                        description="This closes the staged release as failed. Create a new release after fixing its inputs."
                        actionLabel="Reject release"
                        destructive
                        loading={approveRelease.isLoading}
                        onConfirm={() =>
                          approveRelease.mutate({
                            productKey,
                            channel: releaseChannel,
                            id: release.id,
                            approval: { status: "rejected", note: "Rejected in AIX Command Center" },
                          })
                        }
                      />
                    </div>
                  ) : null}
                  {release.state === "approved" ? (
                    <div className="mt-3">
                      <ReleaseActionDialog
                        title={`Activate release v${release.version}?`}
                        description={
                          releaseChannel === "staging"
                            ? `This switches only the noindex dev.vidmax.ai SEO pointer to ${release.pageCount} compiled pages after a fresh immutable-artifact probe.`
                            : `This publishes ${release.pageCount} pages and switches the public vidmax.ai SEO pointer after a fresh immutable-artifact probe.`
                        }
                        actionLabel="Activate release"
                        loading={activateRelease.isLoading}
                        onConfirm={() =>
                          activateRelease.mutate({ productKey, channel: releaseChannel, id: release.id })
                        }
                      />
                    </div>
                  ) : null}
                  {release.state === "superseded" ? (
                    <div className="mt-3">
                      <ReleaseActionDialog
                        title={`Roll back to release v${release.version}?`}
                        description={`This verifies the prior immutable artifacts and switches only the ${releaseChannel} pointer.`}
                        actionLabel="Roll back"
                        destructive
                        loading={rollbackRelease.isLoading}
                        onConfirm={() =>
                          rollbackRelease.mutate({ productKey, channel: releaseChannel, targetReleaseId: release.id })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ))}
              {!releases.isLoading && !releases.data?.rows.length ? (
                <p className="text-sm text-muted-foreground">No release has been proposed.</p>
              ) : null}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!releaseCursor}
                  onClick={() => setReleaseCursor(undefined)}
                >
                  First page
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!releases.data?.nextCursor}
                  onClick={() => setReleaseCursor(releases.data?.nextCursor)}
                >
                  Next page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function SEOReleaseArtifactReview({
  productKey,
  releaseId,
  channel,
}: {
  productKey: string
  releaseId: string
  channel: ReleaseChannel
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [offset, setOffset] = useState(0)
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const inventory = trpc.seo.releases.routeInventory.useQuery(
    { productKey, id: releaseId, channel, query: query.trim() || undefined, limit: 50, offset },
    { enabled: open },
  )
  const preview = trpc.seo.releases.routePreview.useQuery(
    { productKey, id: releaseId, channel, slug: selectedSlug ?? "" },
    { enabled: open && Boolean(selectedSlug) },
  )
  return (
    <section className="mt-3 rounded border border-border p-2">
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(!open)}>
        {open ? "Close artifact review" : "Review exact release artifacts"}
      </Button>
      {open ? (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            These are the digest-verified HTML artifacts in release {releaseId}. They are the exact objects a reviewer
            approves—not the current draft and not a guessed public URL.
          </p>
          <Input
            value={query}
            placeholder="Find route in this release…"
            onChange={(event) => {
              setQuery(event.target.value)
              setOffset(0)
              setSelectedSlug(null)
            }}
          />
          {inventory.error ? <p className="text-xs text-destructive">{inventory.error.message}</p> : null}
          {inventory.isLoading ? <p className="text-xs text-muted-foreground">Reading immutable manifest…</p> : null}
          <div className="max-h-56 space-y-1 overflow-auto">
            {inventory.data?.rows.map((route) => (
              <button
                key={route.slug}
                type="button"
                onClick={() => setSelectedSlug(route.slug)}
                className={`flex w-full items-center gap-2 rounded border p-2 text-left text-xs ${selectedSlug === route.slug ? "border-primary bg-muted" : "border-border"}`}
              >
                <span className="min-w-0 flex-1 truncate font-mono">{route.slug}</span>
                <Badge variant={route.matchesCurrentDraft ? "success" : "outline"}>
                  {route.matchesCurrentDraft ? "matches draft" : "draft differs"}
                </Badge>
                <span className="text-muted-foreground">{route.mediaCount} media</span>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              Previous
            </Button>
            <span className="text-[11px] text-muted-foreground">
              {inventory.data
                ? `${Math.min(offset + 1, inventory.data.total)}–${Math.min(offset + inventory.data.rows.length, inventory.data.total)} of ${inventory.data.total}`
                : "—"}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={!inventory.data?.hasMore}
              onClick={() => setOffset(inventory.data?.nextOffset ?? offset)}
            >
              Next
            </Button>
          </div>
          {selectedSlug ? (
            <div className="space-y-2 border-t border-border pt-3">
              {preview.error ? <p className="text-xs text-destructive">{preview.error.message}</p> : null}
              {preview.isLoading ? <p className="text-xs text-muted-foreground">Verifying HTML digest…</p> : null}
              {preview.data ? (
                <>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <Badge variant={preview.data.matchesCurrentDraft ? "success" : "outline"}>
                      {preview.data.matchesCurrentDraft ? "Same as current draft" : "Current draft has changed"}
                    </Badge>
                    <span>{preview.data.templateVersion}</span>
                    <span className="font-mono" title={preview.data.htmlDigest}>
                      HTML {preview.data.htmlDigest.slice(0, 12)}…
                    </span>
                  </div>
                  <iframe
                    title={`Release ${releaseId} preview for ${selectedSlug}`}
                    srcDoc={preview.data.html}
                    sandbox="allow-popups"
                    className="h-[520px] w-full rounded border border-border bg-white"
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function ReleaseActionDialog({
  title,
  description,
  actionLabel,
  destructive = false,
  loading,
  onConfirm,
}: {
  title: string
  description: string
  actionLabel: string
  destructive?: boolean
  loading: boolean
  onConfirm: () => void
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" size="sm" variant={destructive ? "destructive" : "outline"} loading={loading}>
          {actionLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {description} This action requires an MFA-verified human session.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{actionLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ReleaseReadinessChecklist({
  pageCount,
  assetCount,
  manifestReady,
  probePassed,
  approved,
  active,
}: {
  pageCount: number
  assetCount: number
  manifestReady: boolean
  probePassed: boolean
  approved: boolean
  active: boolean
}) {
  const checks = [
    { label: `${pageCount} gated page(s) compiled`, passed: pageCount > 0 },
    { label: `${assetCount} release asset(s) referenced`, passed: assetCount > 0 },
    { label: "Immutable manifest and digest recorded", passed: manifestReady },
    { label: "Artifact probe passed", passed: probePassed },
    { label: "MFA-verified human approval recorded", passed: approved },
    { label: "Channel pointer activated", passed: active },
  ]
  return (
    <details className="mt-2 rounded bg-muted p-2 text-xs">
      <summary className="cursor-pointer font-medium">Release readiness checklist</summary>
      <ul className="mt-2 space-y-1">
        {checks.map((check) => (
          <li key={check.label} className="flex items-center gap-2">
            <Badge variant={check.passed ? "success" : "outline"}>{check.passed ? "ready" : "waiting"}</Badge>
            <span>{check.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-muted-foreground">
        Content, evidence, media rights, and duplicate checks are enforced before pages enter this release. Approval and
        activation remain separate actions.
      </p>
    </details>
  )
}
